import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './index';

/**
 * Toast notification interface
 */
interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  title?: string;
  duration?: number;
  timestamp: number;
}

/**
 * Modal state interface
 */
interface Modal {
  id: string;
  is_open: boolean;
  data?: any;
}

/**
 * Breadcrumb item interface
 */
interface BreadcrumbItem {
  label: string;
  href?: string;
  is_current?: boolean;
}

/**
 * UI state interface
 */
interface UIState {
  // Sidebar
  sidebar_collapsed: boolean;
  sidebar_mobile_open: boolean;

  // Theme
  theme: 'light' | 'dark';
  
  // Loading overlay
  global_loading: boolean;
  loading_message: string;

  // Toasts/Notifications
  toasts: Toast[];
  toast_position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left' | 'top-center' | 'bottom-center';

  // Modals
  modals: Record<string, Modal>;
  active_modal: string | null;

  // Breadcrumbs
  breadcrumbs: BreadcrumbItem[];

  // Page title
  page_title: string;

  // Confirmation dialog
  confirmation_dialog: {
    is_open: boolean;
    title: string;
    message: string;
    confirm_text: string;
    cancel_text: string;
    confirm_variant: 'danger' | 'primary' | 'success';
    on_confirm?: () => void;
    on_cancel?: () => void;
  };

  // Quick search
  quick_search_open: boolean;

  // Filters panel
  filters_panel_open: boolean;

  // Preferences
  preferences: {
    items_per_page: number;
    date_format: string;
    currency_format: string;
    show_tips: boolean;
    compact_view: boolean;
  };
}

/**
 * Initial state
 */
const initial_state: UIState = {
  sidebar_collapsed: false,
  sidebar_mobile_open: false,
  theme: 'light',
  global_loading: false,
  loading_message: '',
  toasts: [],
  toast_position: 'top-right',
  modals: {},
  active_modal: null,
  breadcrumbs: [],
  page_title: 'Water Management System',
  confirmation_dialog: {
    is_open: false,
    title: '',
    message: '',
    confirm_text: 'Confirm',
    cancel_text: 'Cancel',
    confirm_variant: 'primary',
  },
  quick_search_open: false,
  filters_panel_open: false,
  preferences: {
    items_per_page: 20,
    date_format: 'DD/MM/YYYY',
    currency_format: 'KES',
    show_tips: true,
    compact_view: false,
  },
};

// ============ SLICE ============

const ui_slice = createSlice({
  name: 'ui',
  initialState: initial_state,
  reducers: {
    // ===== SIDEBAR =====

    /**
     * Toggle sidebar collapsed state
     */
    toggle_sidebar: (state) => {
      state.sidebar_collapsed = !state.sidebar_collapsed;
    },

    /**
     * Set sidebar collapsed state
     */
    set_sidebar_collapsed: (state, action: PayloadAction<boolean>) => {
      state.sidebar_collapsed = action.payload;
    },

    /**
     * Toggle mobile sidebar
     */
    toggle_mobile_sidebar: (state) => {
      state.sidebar_mobile_open = !state.sidebar_mobile_open;
    },

    /**
     * Close mobile sidebar
     */
    close_mobile_sidebar: (state) => {
      state.sidebar_mobile_open = false;
    },

    // ===== THEME =====

    /**
     * Toggle theme
     */
    toggle_theme: (state) => {
      state.theme = state.theme === 'light' ? 'dark' : 'light';
    },

    /**
     * Set theme
     */
    set_theme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },

    // ===== LOADING =====

    /**
     * Show global loading overlay
     */
    show_loading: (state, action: PayloadAction<string | undefined>) => {
      state.global_loading = true;
      state.loading_message = action.payload || 'Loading...';
    },

    /**
     * Hide global loading overlay
     */
    hide_loading: (state) => {
      state.global_loading = false;
      state.loading_message = '';
    },

    // ===== TOASTS =====

    /**
     * Show toast notification
     */
    show_toast: (
      state,
      action: PayloadAction<Omit<Toast, 'id' | 'timestamp'>>
    ) => {
      const toast: Toast = {
        ...action.payload,
        id: `toast-${Date.now()}-${Math.random()}`,
        timestamp: Date.now(),
        duration: action.payload.duration || 5000,
      };
      state.toasts.push(toast);
    },

    /**
     * Remove toast by ID
     */
    remove_toast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },

    /**
     * Clear all toasts
     */
    clear_toasts: (state) => {
      state.toasts = [];
    },

    /**
     * Set toast position
     */
    set_toast_position: (
      state,
      action: PayloadAction<UIState['toast_position']>
    ) => {
      state.toast_position = action.payload;
    },

    // ===== MODALS =====

    /**
     * Open modal
     */
    open_modal: (
      state,
      action: PayloadAction<{ id: string; data?: any }>
    ) => {
      state.modals[action.payload.id] = {
        id: action.payload.id,
        is_open: true,
        data: action.payload.data,
      };
      state.active_modal = action.payload.id;
    },

    /**
     * Close modal
     */
    close_modal: (state, action: PayloadAction<string>) => {
      if (state.modals[action.payload]) {
        state.modals[action.payload].is_open = false;
      }
      if (state.active_modal === action.payload) {
        state.active_modal = null;
      }
    },

    /**
     * Close all modals
     */
    close_all_modals: (state) => {
      Object.keys(state.modals).forEach((key) => {
        state.modals[key].is_open = false;
      });
      state.active_modal = null;
    },

    // ===== BREADCRUMBS =====

    /**
     * Set breadcrumbs
     */
    set_breadcrumbs: (state, action: PayloadAction<BreadcrumbItem[]>) => {
      state.breadcrumbs = action.payload;
    },

    /**
     * Clear breadcrumbs
     */
    clear_breadcrumbs: (state) => {
      state.breadcrumbs = [];
    },

    // ===== PAGE TITLE =====

    /**
     * Set page title
     */
    set_page_title: (state, action: PayloadAction<string>) => {
      state.page_title = action.payload;
      // Also update document title
      if (typeof document !== 'undefined') {
        document.title = `${action.payload} - Water Management System`;
      }
    },

    // ===== CONFIRMATION DIALOG =====

    /**
     * Show confirmation dialog
     */
    show_confirmation: (
      state,
      action: PayloadAction<{
        title: string;
        message: string;
        confirm_text?: string;
        cancel_text?: string;
        confirm_variant?: 'danger' | 'primary' | 'success';
        on_confirm?: () => void;
        on_cancel?: () => void;
      }>
    ) => {
      state.confirmation_dialog = {
        is_open: true,
        title: action.payload.title,
        message: action.payload.message,
        confirm_text: action.payload.confirm_text || 'Confirm',
        cancel_text: action.payload.cancel_text || 'Cancel',
        confirm_variant: action.payload.confirm_variant || 'primary',
        on_confirm: action.payload.on_confirm,
        on_cancel: action.payload.on_cancel,
      };
    },

    /**
     * Hide confirmation dialog
     */
    hide_confirmation: (state) => {
      state.confirmation_dialog = {
        ...state.confirmation_dialog,
        is_open: false,
      };
    },

    // ===== QUICK SEARCH =====

    /**
     * Toggle quick search
     */
    toggle_quick_search: (state) => {
      state.quick_search_open = !state.quick_search_open;
    },

    /**
     * Open quick search
     */
    open_quick_search: (state) => {
      state.quick_search_open = true;
    },

    /**
     * Close quick search
     */
    close_quick_search: (state) => {
      state.quick_search_open = false;
    },

    // ===== FILTERS PANEL =====

    /**
     * Toggle filters panel
     */
    toggle_filters_panel: (state) => {
      state.filters_panel_open = !state.filters_panel_open;
    },

    /**
     * Close filters panel
     */
    close_filters_panel: (state) => {
      state.filters_panel_open = false;
    },

    // ===== PREFERENCES =====

    /**
     * Update preferences
     */
    update_preferences: (
      state,
      action: PayloadAction<Partial<UIState['preferences']>>
    ) => {
      state.preferences = {
        ...state.preferences,
        ...action.payload,
      };
    },

    /**
     * Reset preferences to default
     */
    reset_preferences: (state) => {
      state.preferences = initial_state.preferences;
    },
  },
});

// ============ ACTIONS ============

export const {
  toggle_sidebar,
  set_sidebar_collapsed,
  toggle_mobile_sidebar,
  close_mobile_sidebar,
  toggle_theme,
  set_theme,
  show_loading,
  hide_loading,
  show_toast,
  remove_toast,
  clear_toasts,
  set_toast_position,
  open_modal,
  close_modal,
  close_all_modals,
  set_breadcrumbs,
  clear_breadcrumbs,
  set_page_title,
  show_confirmation,
  hide_confirmation,
  toggle_quick_search,
  open_quick_search,
  close_quick_search,
  toggle_filters_panel,
  close_filters_panel,
  update_preferences,
  reset_preferences,
} = ui_slice.actions;

// ============ SELECTORS ============

export const select_ui = (state: RootState) => state.ui;
export const select_sidebar_collapsed = (state: RootState) => state.ui.sidebar_collapsed;
export const select_sidebar_mobile_open = (state: RootState) => state.ui.sidebar_mobile_open;
export const select_theme = (state: RootState) => state.ui.theme;
export const select_global_loading = (state: RootState) => state.ui.global_loading;
export const select_loading_message = (state: RootState) => state.ui.loading_message;
export const select_toasts = (state: RootState) => state.ui.toasts;
export const select_toast_position = (state: RootState) => state.ui.toast_position;
export const select_active_modal = (state: RootState) => state.ui.active_modal;
export const select_breadcrumbs = (state: RootState) => state.ui.breadcrumbs;
export const select_page_title = (state: RootState) => state.ui.page_title;
export const select_confirmation_dialog = (state: RootState) => state.ui.confirmation_dialog;
export const select_quick_search_open = (state: RootState) => state.ui.quick_search_open;
export const select_filters_panel_open = (state: RootState) => state.ui.filters_panel_open;
export const select_preferences = (state: RootState) => state.ui.preferences;

/**
 * Check if a specific modal is open
 */
export const select_is_modal_open = (modal_id: string) => (state: RootState) => {
  return state.ui.modals[modal_id]?.is_open || false;
};

/**
 * Get modal data
 */
export const select_modal_data = (modal_id: string) => (state: RootState) => {
  return state.ui.modals[modal_id]?.data;
};

/**
 * Check if dark mode is enabled
 */
export const select_is_dark_mode = (state: RootState) => {
  return state.ui.theme === 'dark';
};

// ============ HELPER ACTION CREATORS =====

/**
 * Show success toast
 */
export const show_success_toast = (message: string, title?: string) => {
  return show_toast({
    type: 'success',
    message,
    title: title || 'Success',
    duration: 4000,
  });
};

/**
 * Show error toast
 */
export const show_error_toast = (message: string, title?: string) => {
  return show_toast({
    type: 'error',
    message,
    title: title || 'Error',
    duration: 6000,
  });
};

/**
 * Show warning toast
 */
export const show_warning_toast = (message: string, title?: string) => {
  return show_toast({
    type: 'warning',
    message,
    title: title || 'Warning',
    duration: 5000,
  });
};

/**
 * Show info toast
 */
export const show_info_toast = (message: string, title?: string) => {
  return show_toast({
    type: 'info',
    message,
    title: title || 'Info',
    duration: 4000,
  });
};

// ============ EXPORT REDUCER ============

export default ui_slice.reducer;